// src/components/Pages/TowerTopple/towerLogic.ts
var BLOCK_SIZE = 0.28;
var PLATFORM_HEIGHT = 0.15;
var PLATFORM_TOP = PLATFORM_HEIGHT;
var PLATFORM_MARGIN = 0.1;
var BLOCK_GAP = 0.012;
var PITCH = BLOCK_SIZE + BLOCK_GAP;
var FOOTPRINT = 2;
var PLATFORM_HALF = FOOTPRINT * PITCH / 2 + PLATFORM_MARGIN;
var PLATFORM_SIZE = PLATFORM_HALF * 2;
var STACK_JITTER_RAD = 0.06;
var DEFAULT_ROWS = 8;
var DEFAULT_SHOTS = 10;
var KNOCKED_DROP_M = 0.07;
var KNOCKED_SLIDE_M = 0.4;
var MIN_ROWS = 2;
var MAX_ROWS = 10;
var clampRows = (rows) => {
  if (rows === void 0 || !Number.isFinite(rows)) return DEFAULT_ROWS;
  return Math.min(MAX_ROWS, Math.max(MIN_ROWS, Math.round(rows)));
};
var clampShots = (shots) => {
  if (shots === void 0 || !Number.isFinite(shots)) return DEFAULT_SHOTS;
  return Math.min(30, Math.max(3, Math.round(shots)));
};
var MIXED_CYCLE = ["crate", "pumpkin", "ghost"];
var blockKindFor = (blocks, index) => {
  switch (blocks) {
    case "crates":
      return "crate";
    case "pumpkins":
      return "pumpkin";
    case "ghosts":
      return "ghost";
    default:
      return MIXED_CYCLE[index % MIXED_CYCLE.length];
  }
};
function buildTowerLayout(blocks, rowsRaw) {
  const rows = clampRows(rowsRaw);
  const specs = [];
  let index = 0;
  for (let row = 0; row < rows; row++) {
    const y = PLATFORM_TOP + BLOCK_SIZE / 2 + row * PITCH;
    for (let i = 0; i < FOOTPRINT; i++) {
      for (let j = 0; j < FOOTPRINT; j++) {
        const x = (i - (FOOTPRINT - 1) / 2) * PITCH;
        const z = (j - (FOOTPRINT - 1) / 2) * PITCH;
        const yaw = ((index + row) % 2 === 0 ? 1 : -1) * STACK_JITTER_RAD;
        specs.push({ kind: blockKindFor(blocks, index), x, y, z, row, yaw });
        index += 1;
      }
    }
  }
  const height = rows * PITCH;
  return {
    blocks: specs,
    rows,
    centreY: PLATFORM_TOP + height / 2,
    platformHalf: PLATFORM_HALF
  };
}
var isKnockedOff = (x, y, z, platformHalf = PLATFORM_HALF, platformTop = PLATFORM_TOP) => y < platformTop + BLOCK_SIZE / 2 - KNOCKED_DROP_M || Math.abs(x) > platformHalf + KNOCKED_SLIDE_M || Math.abs(z) > platformHalf + KNOCKED_SLIDE_M;
function countStanding(positions, count, platformHalf = PLATFORM_HALF, platformTop = PLATFORM_TOP) {
  let standing = 0;
  for (let i = 0; i < count; i++) {
    const o = i * 7;
    if (!isKnockedOff(
      positions[o],
      positions[o + 1],
      positions[o + 2],
      platformHalf,
      platformTop
    )) {
      standing += 1;
    }
  }
  return standing;
}
var createShotState = (shots) => {
  const capacity = clampShots(shots);
  return { left: capacity, capacity, rebuilds: 0 };
};
var spendShot = (state) => state.left <= 0 ? state : { ...state, left: state.left - 1 };
var canShoot = (state) => state.left > 0;
var shouldRebuild = (state, standing) => state.left <= 0 && standing > 0;
var rebuilt = (state) => ({
  ...state,
  left: state.capacity,
  rebuilds: state.rebuilds + 1
});
var SETTLE_AFTER_LAST_SHOT_S = 3;
var WIN_HOLD_S = 1.2;
var LAUNCH_SPEED_SCALE = 1.8;
var LAUNCH_LOB = 0.14;
var AIM_ASSIST_CONE = 0.5;
var AIM_ASSIST_STRENGTH = 0.45;
var MAX_LIVE_SHOTS = 6;
var SHOT_FLOOR_Y = -3;

// src/components/Pages/TowerTopple/physics.ts
var BODY_STRIDE = 7;
var SHOT_RADIUS = 0.11;
var FIXED_DT = 1 / 60;
var MAX_SUBSTEPS = 4;
var rapierPromise = null;
var loadRapier = () => {
  if (!rapierPromise) {
    rapierPromise = import("@dimforge/rapier3d-compat").then(async (mod) => {
      await mod.init();
      return mod;
    }).catch((error) => {
      rapierPromise = null;
      throw error;
    });
  }
  return rapierPromise;
};
var warmUpRapier = () => {
  void loadRapier().catch(() => void 0);
};
var SHOT_DENSITY = 20;
var BLOCK_DENSITY = 1;
var BLOCK_FRICTION = 0.6;
var BLOCK_RESTITUTION = 0.05;
var SHOT_FRICTION = 0.5;
var SHOT_RESTITUTION = 0.2;
var BLOCK_ROUNDING = 0.01;
var yawQuaternion = (yaw = 0) => ({
  x: 0,
  y: Math.sin(yaw / 2),
  z: 0,
  w: Math.cos(yaw / 2)
});
function createTowerWorld(RAPIER, layout) {
  const world = new RAPIER.World({ x: 0, y: -9.81, z: 0 });
  world.timestep = FIXED_DT;
  world.createCollider(
    RAPIER.ColliderDesc.cuboid(
      layout.platformHalf,
      PLATFORM_HEIGHT / 2,
      layout.platformHalf
    ).setTranslation(0, PLATFORM_HEIGHT / 2, 0).setFriction(BLOCK_FRICTION)
  );
  world.createCollider(
    RAPIER.ColliderDesc.cuboid(6, 0.05, 6).setTranslation(0, -0.05, 0).setFriction(BLOCK_FRICTION)
  );
  const half = BLOCK_SIZE / 2 - BLOCK_ROUNDING;
  const blocks = layout.blocks.map((spec) => {
    const body = world.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic().setTranslation(spec.x, spec.y, spec.z).setRotation(yawQuaternion(spec.yaw)).setAngularDamping(0.15)
    );
    world.createCollider(
      RAPIER.ColliderDesc.roundCuboid(half, half, half, BLOCK_ROUNDING).setDensity(BLOCK_DENSITY).setFriction(BLOCK_FRICTION).setRestitution(BLOCK_RESTITUTION),
      body
    );
    return body;
  });
  let shots = [];
  let accumulator = 0;
  let disposed = false;
  const removeShot = (body) => {
    world.removeRigidBody(body);
  };
  const readInto = (body, out, offset) => {
    const t = body.translation();
    const r = body.rotation();
    out[offset] = t.x;
    out[offset + 1] = t.y;
    out[offset + 2] = t.z;
    out[offset + 3] = r.x;
    out[offset + 4] = r.y;
    out[offset + 5] = r.z;
    out[offset + 6] = r.w;
  };
  return {
    blockCount: blocks.length,
    step(dt) {
      if (disposed) return;
      accumulator = Math.min(accumulator + dt, FIXED_DT * MAX_SUBSTEPS);
      while (accumulator >= FIXED_DT) {
        world.step();
        accumulator -= FIXED_DT;
      }
      if (shots.length > 0) {
        const keep = [];
        for (const shot of shots) {
          if (shot.translation().y < SHOT_FLOOR_Y) removeShot(shot);
          else keep.push(shot);
        }
        if (keep.length !== shots.length) shots = keep;
      }
    },
    launch(origin, velocity) {
      if (disposed) return;
      while (shots.length >= MAX_LIVE_SHOTS) {
        const oldest = shots.shift();
        if (oldest) removeShot(oldest);
      }
      const body = world.createRigidBody(
        RAPIER.RigidBodyDesc.dynamic().setTranslation(origin.x, origin.y, origin.z).setLinvel(velocity.x, velocity.y, velocity.z).setCcdEnabled(true)
      );
      world.createCollider(
        RAPIER.ColliderDesc.ball(SHOT_RADIUS).setDensity(SHOT_DENSITY).setFriction(SHOT_FRICTION).setRestitution(SHOT_RESTITUTION),
        body
      );
      shots.push(body);
    },
    readBlocks(out) {
      for (let i = 0; i < blocks.length; i++) {
        readInto(blocks[i], out, i * BODY_STRIDE);
      }
    },
    readShots(out) {
      const count = Math.min(shots.length, out.length / BODY_STRIDE);
      for (let i = 0; i < count; i++) {
        readInto(shots[i], out, i * BODY_STRIDE);
      }
      return count;
    },
    rebuild() {
      if (disposed) return;
      for (const shot of shots) removeShot(shot);
      shots = [];
      accumulator = 0;
      blocks.forEach((body, i) => {
        const spec = layout.blocks[i];
        body.setTranslation({ x: spec.x, y: spec.y, z: spec.z }, true);
        body.setRotation(yawQuaternion(spec.yaw), true);
        body.setLinvel({ x: 0, y: 0, z: 0 }, true);
        body.setAngvel({ x: 0, y: 0, z: 0 }, true);
      });
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      world.free();
    }
  };
}

export {
  BLOCK_SIZE,
  PLATFORM_HEIGHT,
  buildTowerLayout,
  countStanding,
  createShotState,
  spendShot,
  canShoot,
  shouldRebuild,
  rebuilt,
  SETTLE_AFTER_LAST_SHOT_S,
  WIN_HOLD_S,
  LAUNCH_SPEED_SCALE,
  LAUNCH_LOB,
  AIM_ASSIST_CONE,
  AIM_ASSIST_STRENGTH,
  MAX_LIVE_SHOTS,
  BODY_STRIDE,
  SHOT_RADIUS,
  loadRapier,
  warmUpRapier,
  createTowerWorld
};
//# sourceMappingURL=chunk-FZTH6MUO.mjs.map